/**
 * v1 Tenant API client
 * SAIF EXTENSION: Simplified tenant model - settings will be in TenantConfig in future
 */

import { apiClient } from '@/lib/api-client'

// ========================================
// Types
// ========================================

export interface Tenant {
  _id: string
  name: string
  displayName: string
  isActive: boolean
  isDeleted: boolean
  deletedAt: string | null
  createdAt: string
  updatedAt: string
  organizationCount?: number
  userCount?: number
}

export interface TenantListResponse {
  tenants: Tenant[]
  total: number
  page: number
  totalPages: number
  hasMore: boolean
}

export interface TenantUsage {
  organizations: { current: number; limit: number }
  users: { current: number; limit: number }
  agents: { current: number; limit: number }
  prompts: { current: number; limit: number }
  storage: { current: number; limit: number }
}

export interface TenantCreateRequest {
  name: string
  displayName: string
  isActive?: boolean
}

export interface TenantUpdateRequest {
  displayName?: string
  isActive?: boolean
}

export interface TenantOrganization {
  _id: string
  name: string
  displayName: string
  domain: string | null
  isActive: boolean
  userCount: number
  createdAt: string
  updatedAt: string
}

export interface TenantOrganizationsResponse {
  organizations: TenantOrganization[]
  tenantId: string
  tenantName: string
}

export interface TenantListParams {
  page?: number
  limit?: number
  search?: string
  sort_by?: string
  sort_order?: 'asc' | 'desc'
  include_deleted?: boolean
}

// ========================================
// API Client
// ========================================

export const tenantsApi = {
  /**
   * List all tenants with pagination
   */
  list: (params?: TenantListParams) =>
    apiClient.get<{ success: boolean; message: string; data: TenantListResponse }>(
      '/v1/tenants',
      { params }
    ),

  /**
   * Create a new tenant
   */
  create: (data: TenantCreateRequest) =>
    apiClient.post<{ success: boolean; message: string; data: Tenant }>(
      '/v1/tenants',
      data
    ),

  /**
   * Get current user's tenant
   */
  getCurrent: () =>
    apiClient.get<{ success: boolean; message: string; data: Tenant }>(
      '/v1/tenants/current'
    ),

  /**
   * Get tenant by ID
   */
  get: (id: string) =>
    apiClient.get<{ success: boolean; message: string; data: Tenant }>(
      `/v1/tenants/${id}`
    ),

  /**
   * Update tenant
   */
  update: (id: string, data: TenantUpdateRequest) =>
    apiClient.patch<{ success: boolean; message: string; data: Tenant }>(
      `/v1/tenants/${id}`,
      data
    ),

  /**
   * Delete tenant (soft delete)
   */
  delete: (id: string) =>
    apiClient.delete<{ success: boolean; message: string; data: Tenant }>(
      `/v1/tenants/${id}`
    ),

  /**
   * Get organizations for a tenant
   */
  getOrganizations: (id: string, params?: { page?: number; limit?: number; include_deleted?: boolean }) =>
    apiClient.get<{ success: boolean; message: string; data: TenantOrganizationsResponse }>(
      `/v1/tenants/${id}/organizations`,
      { params }
    ),

  /**
   * Get usage statistics for a tenant
   */
  getUsage: (id: string) =>
    apiClient.get<{ success: boolean; message: string; data: TenantUsage }>(
      `/v1/tenants/${id}/usage`
    ),
}

// ========================================
// Convenience functions
// ========================================

export const getTenants = (params?: TenantListParams) => tenantsApi.list(params)
export const getTenant = (id: string) => tenantsApi.get(id)
export const getCurrentTenant = () => tenantsApi.getCurrent()
export const createTenant = (data: TenantCreateRequest) => tenantsApi.create(data)
export const updateTenant = (id: string, data: TenantUpdateRequest) => tenantsApi.update(id, data)
export const deleteTenant = (id: string) => tenantsApi.delete(id)
export const getTenantOrganizations = (id: string) => tenantsApi.getOrganizations(id)
export const getTenantUsage = (id: string) => tenantsApi.getUsage(id)
